import React, { ReactElement, useContext } from 'react'

import { LanguageContext } from '../../Common/Contexts'
import LanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { StatisticAlarm as StatisticAlarmLanguage } from '../../../../i18n'

import Heading from '../../Common/Components/Heading'
import FormRow from '../../Common/Components/FormRow'
import Fieldset from '../../Common/Components/Fieldset'
import { Select, SelectOptions } from '../../Common/Components/Selects'
import TextInput from '../../Common/Components/TextInput'
import { ANY_VALUE_NUMBER_NUMERIC } from '../../Common/Constants/DefaultValues'
import { StatisticsConfiguration } from './Types'
import {
    iREPORT_VALUE_ALARM_REPEAT_TYPE_ONCE,
    iREPORT_VALUE_ALARM_REPEAT_TYPE_PERSISTENT,
    iREPORT_VALUE_ALARM_REPEAT_TYPE_REPEAT_N_TIMES,
    iREPORT_VALUE_ALARM_REPEAT_TYPE_SECONDS,
    iREPORT_VALUE_ALARM_SOUND_TYPE_NONE,
    ISoundInfo,
    pFIELD_ALARM_SOUNDS
} from '../../../NonReact/Alarms/AlarmConstants'

import { playAlarmSoundSample } from '../../Common/Utils/Alarm'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import { SelectOption } from '../../Common/Components/Selects/Types'

// CSS imports. Commented out because of current build process.
// import './fields.scss'

export type StatisticAlarmProps = {
    selectedStatisticsConfiguration: StatisticsConfiguration | undefined
    updateSelectedStatisticsConfiguration: (updatedStatisticsConfiguration: StatisticsConfiguration) => void
}

const StatisticAlarm: React.FC<StatisticAlarmProps> = ({
    selectedStatisticsConfiguration,
    updateSelectedStatisticsConfiguration
}): ReactElement => {
    const language = useContext(LanguageContext) as LanguageHandler
    language.loadBundle('StatisticAlarm')

    const isStatisticsSelected = selectedStatisticsConfiguration !== undefined
    const alarmComparisonType = selectedStatisticsConfiguration?.alarmComparisonType || 0
    const alarmComparisonValue1 = selectedStatisticsConfiguration?.alarmComparisonValue1 || ''
    const alarmComparisonValue2 = selectedStatisticsConfiguration?.alarmComparisonValue2 || ''
    const alarmSoundType = selectedStatisticsConfiguration?.alarmSoundType || ANY_VALUE_NUMBER_NUMERIC
    const alarmRepeatType = selectedStatisticsConfiguration?.alarmRepeatType || iREPORT_VALUE_ALARM_REPEAT_TYPE_ONCE
    const alarmSettingsValue = selectedStatisticsConfiguration?.alarmSettingsValue || 0

    const updateAlarmComparison = (value: number): void => {
        if (selectedStatisticsConfiguration) {
            updateSelectedStatisticsConfiguration({
                ...selectedStatisticsConfiguration,
                alarmComparisonType: value
            })
        }
    }

    const updateAlarmComparisonValue1 = (value: string): void => {
        if (selectedStatisticsConfiguration) {
            updateSelectedStatisticsConfiguration({
                ...selectedStatisticsConfiguration,
                alarmComparisonValue1: value
            })
        }
    }

    const updateAlarmComparisonValue2 = (value: string): void => {
        if (selectedStatisticsConfiguration) {
            updateSelectedStatisticsConfiguration({
                ...selectedStatisticsConfiguration,
                alarmComparisonValue2: value
            })
        }
    }

    const updateAlarmSoundType = (value: number): void => {
        if (selectedStatisticsConfiguration) {
            updateSelectedStatisticsConfiguration({
                ...selectedStatisticsConfiguration,
                alarmSoundType: value
            })

            if (value !== iREPORT_VALUE_ALARM_SOUND_TYPE_NONE) {
                playAlarmSoundSample(value)
            }
        }
    }

    const updateAlarmRepeat = (value: number): void => {
        if (selectedStatisticsConfiguration) {
            updateSelectedStatisticsConfiguration({
                ...selectedStatisticsConfiguration,
                alarmRepeatType: value
            })
        }
    }
    const updateAlarmSettingsValue = (value: string): void => {
        if (selectedStatisticsConfiguration) {
            updateSelectedStatisticsConfiguration({
                ...selectedStatisticsConfiguration,
                alarmSettingsValue: parseInt(value, 10) || 0
            })
        }
    }

    const comparisonTypeOptions: SelectOptions<number> = [
        { label: language?.get(StatisticAlarmLanguage.ComparisonTypeOptionNone), value: 0 },
        { label: language?.get(StatisticAlarmLanguage.ComparisonTypeOptionHigherThan), value: 1 },
        { label: language?.get(StatisticAlarmLanguage.ComparisonTypeOptionHigherEqual), value: 2 },
        { label: language?.get(StatisticAlarmLanguage.ComparisonTypeOptionLowerThan), value: 3 },
        { label: language?.get(StatisticAlarmLanguage.ComparisonTypeOptionLowerEqual), value: 4 },
        { label: language?.get(StatisticAlarmLanguage.ComparisonTypeOptionBetween), value: 5 },
        { label: language?.get(StatisticAlarmLanguage.ComparisonTypeOptionEqual), value: 6 },
        { label: language?.get(StatisticAlarmLanguage.ComparisonTypeOptionNotEqual), value: 7 },
        { label: language?.get(StatisticAlarmLanguage.ComparisonTypeOptionNotBetween), value: 8 }
    ]

    const alarmSoundTypes: SelectOptions<number> = [
        { label: language?.get(StatisticAlarmLanguage.AlarmTypeNoSound), value: iREPORT_VALUE_ALARM_SOUND_TYPE_NONE },
        ...pFIELD_ALARM_SOUNDS
            .map((sound: ISoundInfo, index: number) => ({
                label: language?.get(
                    (StatisticAlarmLanguage as unknown as Record<string, string>)[`AlarmType_${sound.sDescription}`]
                ),
                value: index + 1
            }))
            .sort((a: SelectOption<number>, b: SelectOption<number>) => a.label.localeCompare(b.label))
    ]

    const repeatTypes = [
        { label: language?.get(StatisticAlarmLanguage.AlarmRepeatOnce), value: iREPORT_VALUE_ALARM_REPEAT_TYPE_ONCE },
        {
            label: language?.get(StatisticAlarmLanguage.AlarmRepeatSeconds),
            value: iREPORT_VALUE_ALARM_REPEAT_TYPE_SECONDS
        },
        {
            label: language?.get(StatisticAlarmLanguage.AlarmRepeatPersistent),
            value: iREPORT_VALUE_ALARM_REPEAT_TYPE_PERSISTENT
        },
        {
            label: language?.get(StatisticAlarmLanguage.AlarmRepeatRepeat),
            value: iREPORT_VALUE_ALARM_REPEAT_TYPE_REPEAT_N_TIMES
        }
    ]

    return (
        <Fieldset className='statistic-alarm'>
            <Heading level={HeadingLevel.TWO}>
                {language?.get(StatisticAlarmLanguage.StatisticAlarm_SectionHeader)}
            </Heading>

            <FormRow>
                <Select
                    label={language?.get(StatisticAlarmLanguage.ComparisonTypeLabel)}
                    options={comparisonTypeOptions}
                    handleChange={updateAlarmComparison}
                    value={alarmComparisonType}
                    disabled={!isStatisticsSelected}
                />
            </FormRow>

            {alarmComparisonType !== 0 && (
                <FormRow isInline>
                    <div className='alarm-value'>
                        <TextInput
                            value={alarmComparisonValue1}
                            onChange={updateAlarmComparisonValue1}
                            labelLeft={language?.get(StatisticAlarmLanguage.ComparisonTypeValue)}
                            placeholder='0'
                            disabled={!isStatisticsSelected}
                        />

                        {(alarmComparisonType === 5 || alarmComparisonType === 8) && (
                            <TextInput
                                value={alarmComparisonValue2}
                                onChange={updateAlarmComparisonValue2}
                                labelLeft={language?.get(StatisticAlarmLanguage.ComparisonTypeAnd)}
                                placeholder='100'
                                disabled={!isStatisticsSelected}
                            />
                        )}
                    </div>
                </FormRow>
            )}

            <FormRow>
                <Select
                    label={language?.get(StatisticAlarmLanguage.AlarmTypeLabel)}
                    options={alarmSoundTypes}
                    handleChange={updateAlarmSoundType}
                    value={alarmSoundType}
                    disabled={!isStatisticsSelected || alarmComparisonType === 0}
                />
            </FormRow>

            {alarmSoundType !== iREPORT_VALUE_ALARM_SOUND_TYPE_NONE && (
                <React.Fragment>
                    <FormRow>
                        <Select
                            label={language?.get(StatisticAlarmLanguage.AlarmRepeatTypeLabel)}
                            options={repeatTypes}
                            handleChange={updateAlarmRepeat}
                            value={alarmRepeatType}
                            disabled={!isStatisticsSelected}
                        />
                    </FormRow>
                    <FormRow>
                        {alarmRepeatType !== iREPORT_VALUE_ALARM_REPEAT_TYPE_ONCE &&
                            alarmRepeatType !== iREPORT_VALUE_ALARM_REPEAT_TYPE_PERSISTENT && (
                                <TextInput
                                    labelLeft={language?.get(StatisticAlarmLanguage.AlarmSettingsLabel)}
                                    onChange={updateAlarmSettingsValue}
                                    type={'number'}
                                    placeholder={'0'}
                                    value={alarmSettingsValue}
                                    disabled={!isStatisticsSelected}
                                />
                            )}
                    </FormRow>
                </React.Fragment>
            )}
        </Fieldset>
    )
}

export default StatisticAlarm
